import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import PaymentButton from "@/components/PaymentButton";
import BrochureDialog from "@/components/BrochureDialog";
import { ArrowUpRight, Calendar, MapPin, Sparkles, ShieldCheck, Download, CheckCircle2 } from "lucide-react";
import { trackEvent, getStoredAttribution, metaTrack } from "@/lib/analytics";
import { usePageMeta } from "@/lib/pageMeta";

/* Deep-green / emerald / cream Climate Power Summit visual identity — same as CpsApplyPage */
const CPS_STYLE = {
    "--cps-forest": "#0F2A22",
    "--cps-forest-2": "#143A2C",
    "--cps-emerald": "#1F6E4C",
    "--cps-lime": "#A7C957",
    "--cps-cream": "#F5EFD8",
    "--cps-cream-dim": "rgba(245,239,216,0.72)",
    "--cps-border": "rgba(245,239,216,0.18)",
};

const HAWA_MAHAL_IMG =
    "https://images.unsplash.com/photo-1757310062384-d3bcfe3026e8?w=1600&q=80&fm=jpg&fit=crop";

const CPS_BROCHURE_URL =
    "https://customer-assets-v7afamib.emergentagent.net/job_f4c7dbec-80a4-4d4b-81c9-de1d792e1fe8/artifacts/yudn4mgt_CPS%20.pdf";

const INCLUDED = [
    "Three nights shared accommodation · check-in 13 Nov, check-out 16 Nov 2026",
    "Selected meals as per the official summit itinerary",
    "Access to summit conference sessions, panels and workshops",
    "Networking and delegate engagement opportunities",
    "Day 3 organised sustainability activities",
    "Participation certificate · official delegate kit & materials",
];

const EXCLUDED = [
    "Airfare and travel to Jaipur",
    "Airport pickup and drop-off",
    "Local transport unless in the final itinerary",
    "Visa costs, personal expenses, off-itinerary meals",
];

const DIETARY_OPTIONS = ["No preference", "Vegetarian", "Vegan", "Jain", "Halal", "Gluten-free", "Other"];

export default function SelfFundedRegisterPage() {
    const [params] = useSearchParams();
    const resumeRef = params.get("resume") || "";
    const resumeEmail = params.get("e") || "";

    const [statusInfo, setStatusInfo] = useState(null);
    const [form, setForm] = useState({
        full_name: "", email: "", whatsapp: "", nationality: "", residence_country: "",
        institution: "", designation: "", dob: "", dietary: "No preference",
        accommodation: "", emergency_contact: "",
    });
    const [consents, setConsents] = useState({ terms: false, privacy: false, refund: false });
    const [submitting, setSubmitting] = useState(false);
    const [registration, setRegistration] = useState(null); // { id, ref }
    const [confirmed, setConfirmed] = useState(null); // registration record after verified payment
    const [brochure, setBrochure] = useState(null);
    const [resumeForm, setResumeForm] = useState({ ref: resumeRef, email: resumeEmail });
    const [resuming, setResuming] = useState(false);

    usePageMeta({
        title: "Self-Funded Delegate Registration — ₹32,000 | Climate Power Summit 2026 | ZSGI",
        description: "Register directly as a Self-Funded Delegate for Climate Power Summit 2026 in Jaipur, India — 13–15 November 2026. ₹32,000 INR full registration.",
        ogTitle: "Self-Funded Delegate Registration | Climate Power Summit 2026",
        ogDescription: "Three days of climate leadership, global networking and meaningful action in Jaipur, India.",
        twitterTitle: "Self-Funded Delegate Registration | Climate Power Summit 2026",
        twitterDescription: "Register directly for Climate Power Summit 2026 in Jaipur — ₹32,000 INR.",
        canonical: "https://zsgi.org/apply/cps-2026/self-funded",
    });

    useEffect(() => {
        trackEvent("CPS_SelfFunded_Page_View", { program_slug: "cps-2026" });
        metaTrack("ViewContent", {
            content_ids: ["cps-2026-self-funded"],
            content_name: "Climate Power Summit 2026 — Self-Funded Registration",
            content_category: "cps-2026-self-funded",
            content_type: "product",
            currency: "INR",
            value: 32000,
        }, { dedupKey: "ViewContent:cps-2026-self-funded" });
        api.get("/cps/self-funded/status")
            .then(({ data }) => setStatusInfo(data))
            .catch(() => setStatusInfo({ open: true, seats_left: null }));
    }, []);

    // Auto-resume when arriving via the email link (?resume=…&e=…)
    useEffect(() => {
        if (resumeRef && resumeEmail) doResume(resumeRef, resumeEmail);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    const toggleConsent = (k) => (e) => setConsents({ ...consents, [k]: e.target.checked });

    const submit = async (e) => {
        e.preventDefault();
        if (!(consents.terms && consents.privacy && consents.refund)) {
            toast.error("Please accept the terms, privacy policy and refund policy to continue.");
            return;
        }
        setSubmitting(true);
        try {
            const attribution = getStoredAttribution();
            const { data } = await api.post("/cps/self-funded/register", {
                ...form,
                consent_terms: consents.terms,
                consent_privacy: consents.privacy,
                consent_refund: consents.refund,
                attribution,
            });
            setRegistration({ id: data.id, ref: data.ref });
            trackEvent("CPS_SelfFunded_Registration_Submitted", { ref: data.ref });
            metaTrack("CompleteRegistration", {
                content_category: "cps-2026-self-funded",
                content_name: "CPS 2026 — Self-Funded Registration Submitted",
                content_ids: ["cps-2026-self-funded"],
                currency: "INR",
                value: 32000,
            }, { dedupKey: `CompleteRegistration:${data.id}` });
            toast.success(data.resumed ? "Registration found — continue to payment" : "Registration saved — complete payment to confirm");
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not submit. Try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const doResume = async (ref, email) => {
        setResuming(true);
        try {
            const { data } = await api.post("/cps/self-funded/resume", { ref, email });
            const reg = data.registration;
            if (reg.status === "confirmed") {
                setConfirmed(reg);
            } else {
                setForm((f) => ({ ...f, full_name: reg.full_name, email: reg.email, whatsapp: reg.whatsapp || "" }));
                setRegistration({ id: reg.id, ref: reg.ref });
                toast.success(`Welcome back — registration ${reg.ref} resumed`);
            }
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not find that registration.");
        } finally {
            setResuming(false);
        }
    };

    const firePaymentInitiated = () => {
        trackEvent("CPS_SelfFunded_Payment_Initiated", { ref: registration?.ref, value: 32000, currency: "INR" });
        metaTrack("InitiateCheckout", {
            content_ids: ["cps-2026-self-funded"],
            content_category: "cps-2026-self-funded",
            content_name: "CPS 2026 — Self-Funded Delegate Registration",
            content_type: "product",
            currency: "INR",
            value: 32000,
            num_items: 1,
        });
    };

    const onPaid = () => {
        trackEvent("CPS_SelfFunded_Payment_Success", { ref: registration?.ref, value: 32000, currency: "INR" });
        // Browser Purchase — event_id matches the server-side CAPI fire from
        // /payments/verify (cps-purchase-<registration_id>) so Meta dedupes.
        metaTrack("Purchase", {
            content_ids: ["cps-2026-self-funded"],
            content_category: "cps-2026-self-funded",
            content_name: "CPS 2026 — Self-Funded Delegate Registration",
            content_type: "product",
            currency: "INR",
            value: 32000,
            num_items: 1,
        }, { dedupKey: `Purchase:${registration?.id}`, eventId: `cps-purchase-${registration?.id}` });
        setConfirmed({ ref: registration?.ref, full_name: form.full_name });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const onPaymentFailed = async (resp) => {
        if (!registration?.ref) return;
        try {
            await api.post("/cps/self-funded/payment-failed", {
                ref: registration.ref,
                reason: resp?.error?.description || "Payment failed at checkout",
            });
        } catch (_e) { /* best-effort */ }
    };

    const isClosed = statusInfo && !statusInfo.open;

    return (
        <section
            data-testid="self-funded-page"
            style={CPS_STYLE}
            className="relative min-h-screen pt-28 md:pt-36 pb-24 md:pb-40"
        >
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, var(--cps-forest) 0%, var(--cps-forest-2) 40%, var(--cps-forest) 100%)" }} />
            <div className="absolute inset-x-0 top-0 h-[70vh] opacity-[0.18]">
                <img src={HAWA_MAHAL_IMG} alt="Hawa Mahal, Jaipur" className="w-full h-full object-cover" />
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 0%, var(--cps-forest) 90%)" }} />
            </div>
            <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "radial-gradient(circle at 15% 15%, var(--cps-lime) 0, transparent 40%), radial-gradient(circle at 85% 85%, var(--cps-emerald) 0, transparent 50%)" }} />

            <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
                {/* Header */}
                <div className="mb-10 md:mb-14">
                    <div className="label-mono flex items-center gap-2" style={{ color: "var(--cps-lime)" }} data-testid="sf-eyebrow">
                        <Sparkles className="w-3 h-3" />
                        SELF-FUNDED REGISTRATIONS NOW OPEN
                    </div>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
                        className="serif-display text-4xl md:text-6xl mt-4 leading-[1.02] max-w-4xl"
                        style={{ color: "var(--cps-cream)" }}
                    >
                        Self-Funded Delegate —{" "}
                        <span className="serif-italic" style={{ color: "var(--cps-lime)" }}>₹32,000 INR</span>
                    </motion.h1>
                    <p className="mt-4 serif-italic text-lg md:text-xl" style={{ color: "var(--cps-lime)" }}>
                        Three Days of Climate Leadership, Global Networking and Meaningful Action.
                    </p>
                    <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3">
                        <div className="flex items-center gap-2 label-mono" style={{ color: "var(--cps-cream-dim)" }}>
                            <MapPin className="w-3.5 h-3.5" style={{ color: "var(--cps-lime)" }} /> Jaipur, India
                        </div>
                        <div className="flex items-center gap-2 label-mono" style={{ color: "var(--cps-cream-dim)" }}>
                            <Calendar className="w-3.5 h-3.5" style={{ color: "var(--cps-lime)" }} /> 13–15 November 2026
                        </div>
                        <div className="flex items-center gap-2 label-mono" style={{ color: "var(--cps-cream-dim)" }} data-testid="sf-seats-left">
                            <ShieldCheck className="w-3.5 h-3.5" style={{ color: "var(--cps-lime)" }} />
                            {statusInfo?.seats_left != null ? `${statusInfo.seats_left} seats remaining` : "Direct registration · no interview"}
                        </div>
                    </div>
                </div>

                <div className="grid md:grid-cols-12 gap-10">
                    {/* Left: form / pay / confirmed / closed */}
                    <div className="md:col-span-7">
                        {confirmed ? (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="p-8 md:p-10"
                                style={{ background: "rgba(15,42,34,0.55)", border: "1px solid var(--cps-border)" }}
                                data-testid="sf-confirmed"
                            >
                                <CheckCircle2 className="w-10 h-10" style={{ color: "var(--cps-lime)" }} />
                                <h2 className="serif-display text-3xl md:text-4xl mt-4" style={{ color: "var(--cps-cream)" }}>
                                    Registration confirmed{confirmed.full_name ? `, ${confirmed.full_name.split(" ")[0]}` : ""}.
                                </h2>
                                <p className="mt-3" style={{ color: "var(--cps-cream-dim)" }}>
                                    Your place at Climate Power Summit 2026 (13–15 November 2026, Jaipur) is confirmed.
                                    A confirmation email with your registration details is on its way to your inbox.
                                </p>
                                <div className="mt-8 p-6 border" style={{ borderColor: "var(--cps-border)", background: "rgba(167,201,87,0.08)" }}>
                                    <div className="label-mono text-[10px]" style={{ color: "var(--cps-cream-dim)" }}>YOUR REGISTRATION ID</div>
                                    <div className="serif-display text-3xl md:text-4xl mt-2" style={{ color: "var(--cps-lime)" }} data-testid="sf-confirmed-ref">
                                        {confirmed.ref}
                                    </div>
                                </div>
                                <p className="mt-6 label-mono text-[10px]" style={{ color: "var(--cps-cream-dim)" }}>
                                    Accommodation, reporting instructions and the final itinerary will be shared by the organising team.
                                </p>
                            </motion.div>
                        ) : isClosed ? (
                            <div
                                className="p-8 md:p-10"
                                style={{ background: "rgba(15,42,34,0.55)", border: "1px solid var(--cps-border)" }}
                                data-testid="sf-closed"
                            >
                                <div className="label-mono" style={{ color: "var(--cps-lime)" }}>— REGISTRATIONS CLOSED</div>
                                <h2 className="serif-display text-3xl md:text-4xl mt-4" style={{ color: "var(--cps-cream)" }}>
                                    Self-funded seats are currently unavailable.
                                </h2>
                                <p className="mt-3" style={{ color: "var(--cps-cream-dim)" }}>
                                    The self-funded category is closed at the moment. You can still apply through the
                                    scholarship pathway, or contact the organising team.
                                </p>
                                <div className="mt-8 flex flex-wrap gap-3">
                                    <Link to="/apply/cps-2026" className="inline-flex items-center gap-2 px-6 py-3.5 label-mono text-[11px] font-medium transition-all hover:scale-[1.02]" style={{ background: "var(--cps-lime)", color: "var(--cps-forest)" }} data-testid="sf-closed-scholarship-link">
                                        Apply via scholarship pathway <ArrowUpRight className="w-4 h-4" />
                                    </Link>
                                    <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3.5 label-mono text-[11px] border transition-colors" style={{ borderColor: "var(--cps-border)", color: "var(--cps-cream)" }} data-testid="sf-closed-contact-link">
                                        Contact the team
                                    </Link>
                                </div>
                            </div>
                        ) : !registration ? (
                            <form onSubmit={submit} className="space-y-8" data-testid="sf-form">
                                <div>
                                    <label className="label-mono block mb-2" style={{ color: "var(--cps-lime)" }}>Full name (as required on certificate)</label>
                                    <input required className="input-editorial" value={form.full_name} onChange={change("full_name")} data-testid="sf-name" />
                                </div>
                                <div className="grid md:grid-cols-2 gap-8">
                                    <div>
                                        <label className="label-mono block mb-2" style={{ color: "var(--cps-lime)" }}>Email</label>
                                        <input required type="email" className="input-editorial" value={form.email} onChange={change("email")} data-testid="sf-email" />
                                    </div>
                                    <div>
                                        <label className="label-mono block mb-2" style={{ color: "var(--cps-lime)" }}>WhatsApp number (with country code)</label>
                                        <input required className="input-editorial" value={form.whatsapp} onChange={change("whatsapp")} placeholder="+91…" data-testid="sf-whatsapp" />
                                    </div>
                                </div>
                                <div className="grid md:grid-cols-2 gap-8">
                                    <div>
                                        <label className="label-mono block mb-2" style={{ color: "var(--cps-lime)" }}>Nationality</label>
                                        <input required className="input-editorial" value={form.nationality} onChange={change("nationality")} data-testid="sf-nationality" />
                                    </div>
                                    <div>
                                        <label className="label-mono block mb-2" style={{ color: "var(--cps-lime)" }}>Country of residence</label>
                                        <input required className="input-editorial" value={form.residence_country} onChange={change("residence_country")} data-testid="sf-residence" />
                                    </div>
                                </div>
                                <div className="grid md:grid-cols-2 gap-8">
                                    <div>
                                        <label className="label-mono block mb-2" style={{ color: "var(--cps-lime)" }}>Institution / organisation</label>
                                        <input className="input-editorial" value={form.institution} onChange={change("institution")} data-testid="sf-institution" />
                                    </div>
                                    <div>
                                        <label className="label-mono block mb-2" style={{ color: "var(--cps-lime)" }}>Designation / student status</label>
                                        <input className="input-editorial" value={form.designation} onChange={change("designation")} placeholder="e.g. Student, Researcher" data-testid="sf-designation" />
                                    </div>
                                </div>
                                <div className="grid md:grid-cols-2 gap-8">
                                    <div>
                                        <label className="label-mono block mb-2" style={{ color: "var(--cps-lime)" }}>Date of birth</label>
                                        <input type="date" className="input-editorial" value={form.dob} onChange={change("dob")} data-testid="sf-dob" />
                                    </div>
                                    <div>
                                        <label className="label-mono block mb-2" style={{ color: "var(--cps-lime)" }}>Dietary preference</label>
                                        <select className="input-editorial" value={form.dietary} onChange={change("dietary")} data-testid="sf-dietary">
                                            {DIETARY_OPTIONS.map((d) => <option key={d} value={d} className="bg-[var(--cps-forest)]">{d}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <div>
                                    <label className="label-mono block mb-2" style={{ color: "var(--cps-lime)" }}>Accommodation preferences / special requirements (optional)</label>
                                    <textarea rows={3} className="input-editorial resize-none" value={form.accommodation} onChange={change("accommodation")} data-testid="sf-accommodation" />
                                </div>
                                <div>
                                    <label className="label-mono block mb-2" style={{ color: "var(--cps-lime)" }}>Emergency contact (name & number)</label>
                                    <input required className="input-editorial" value={form.emergency_contact} onChange={change("emergency_contact")} data-testid="sf-emergency" />
                                </div>

                                {/* Consents */}
                                <div className="space-y-4 p-6 border" style={{ borderColor: "var(--cps-border)", background: "rgba(15,42,34,0.4)" }} data-testid="sf-consents">
                                    {[
                                        { key: "terms", label: "I agree to the Climate Power Summit 2026 event terms.", testid: "sf-consent-terms" },
                                        { key: "privacy", label: "I consent to my details being processed under the privacy policy.", testid: "sf-consent-privacy" },
                                        { key: "refund", label: "I acknowledge the ₹32,000 registration fee is non-refundable under the published CPS refund policy.", testid: "sf-consent-refund" },
                                    ].map((c) => (
                                        <label key={c.key} className="flex items-start gap-3 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={consents[c.key]}
                                                onChange={toggleConsent(c.key)}
                                                className="mt-1 w-4 h-4 accent-[#A7C957]"
                                                data-testid={c.testid}
                                            />
                                            <span className="text-sm" style={{ color: "var(--cps-cream-dim)" }}>{c.label}</span>
                                        </label>
                                    ))}
                                </div>

                                <button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="sf-submit"
                                    className="inline-flex items-center gap-2 px-6 py-4 label-mono text-[11px] font-medium transition-all hover:scale-[1.02] disabled:opacity-40 disabled:hover:scale-100"
                                    style={{ background: "var(--cps-lime)", color: "var(--cps-forest)" }}
                                >
                                    {submitting ? "Saving…" : "Continue to Payment · ₹32,000"} <ArrowUpRight className="w-4 h-4" />
                                </button>

                                {/* Resume panel */}
                                <div className="pt-6 border-t" style={{ borderColor: "var(--cps-border)" }}>
                                    <div className="label-mono text-[10px] mb-3" style={{ color: "var(--cps-cream-dim)" }}>
                                        ALREADY REGISTERED? RESUME YOUR PAYMENT
                                    </div>
                                    <div className="flex flex-col sm:flex-row gap-3">
                                        <input
                                            className="input-editorial flex-1"
                                            placeholder="Registration ID (CPS26-SF-…)"
                                            value={resumeForm.ref}
                                            onChange={(e) => setResumeForm({ ...resumeForm, ref: e.target.value })}
                                            data-testid="sf-resume-ref"
                                        />
                                        <input
                                            className="input-editorial flex-1"
                                            placeholder="Email used"
                                            value={resumeForm.email}
                                            onChange={(e) => setResumeForm({ ...resumeForm, email: e.target.value })}
                                            data-testid="sf-resume-email"
                                        />
                                        <button
                                            type="button"
                                            disabled={resuming || !resumeForm.ref || !resumeForm.email}
                                            onClick={() => doResume(resumeForm.ref, resumeForm.email)}
                                            className="px-6 py-3 label-mono text-[11px] border transition-colors disabled:opacity-40"
                                            style={{ borderColor: "var(--cps-border)", color: "var(--cps-cream)" }}
                                            data-testid="sf-resume-btn"
                                        >
                                            {resuming ? "Finding…" : "Resume"}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        ) : (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="p-8 md:p-10"
                                style={{ background: "rgba(15,42,34,0.55)", border: "1px solid var(--cps-border)" }}
                                data-testid="sf-pay-stage"
                            >
                                <div className="label-mono" style={{ color: "var(--cps-lime)" }}>
                                    — REGISTRATION <span data-testid="sf-pay-ref">{registration.ref}</span>
                                </div>
                                <h2 className="serif-display text-3xl md:text-4xl mt-4" style={{ color: "var(--cps-cream)" }}>
                                    Almost there{form.full_name ? `, ${form.full_name.split(" ")[0]}` : ""}.
                                </h2>
                                <p className="mt-3" style={{ color: "var(--cps-cream-dim)" }}>
                                    Complete the ₹32,000 registration payment to confirm your seat at Climate Power Summit 2026.
                                    Your registration is held as <strong>payment pending</strong> until then.
                                </p>

                                <div className="mt-8 pt-8 border-t" style={{ borderColor: "var(--cps-border)" }}>
                                    <div className="label-mono text-[10px]" style={{ color: "var(--cps-cream-dim)" }}>FULL REGISTRATION FEE</div>
                                    <div className="serif-display text-5xl md:text-6xl leading-none mt-2" style={{ color: "var(--cps-lime)" }} data-testid="sf-price-display">
                                        ₹32,000
                                    </div>
                                    <div className="label-mono text-[10px] mt-2" style={{ color: "var(--cps-cream-dim)" }}>
                                        One payment · no separate application fee · INR
                                    </div>

                                    <div className="mt-6">
                                        <PaymentButton
                                            purpose="cps_self_funded"
                                            item="cps-2026-self-funded"
                                            name={form.full_name}
                                            email={form.email}
                                            phone={form.whatsapp}
                                            application_id={registration.id}
                                            label="Register & Pay ₹32,000"
                                            className="inline-flex items-center gap-2 w-full justify-center px-6 py-4 label-mono text-[11px] font-medium transition-all hover:scale-[1.01]"
                                            testid="sf-pay-btn"
                                            onBeforePay={firePaymentInitiated}
                                            onPaid={onPaid}
                                            onFailed={onPaymentFailed}
                                        />
                                    </div>

                                    <div
                                        className="mt-6 py-3 px-4 label-mono text-[11px] flex items-center gap-2"
                                        style={{ background: "rgba(167,201,87,0.1)", color: "var(--cps-cream)", border: "1px solid rgba(167,201,87,0.25)" }}
                                    >
                                        <ShieldCheck className="w-3.5 h-3.5" style={{ color: "var(--cps-lime)" }} />
                                        Secure Razorpay checkout · payment verified server-side · non-refundable per CPS policy
                                    </div>
                                    <p className="mt-4 label-mono text-[10px]" style={{ color: "var(--cps-cream-dim)" }}>
                                        If your payment fails, you can retry right here — your registration {registration.ref} stays saved.
                                    </p>
                                </div>
                            </motion.div>
                        )}
                    </div>

                    {/* Right rail */}
                    <aside className="md:col-span-5 md:pl-4">
                        <div
                            className="p-6 md:p-8"
                            style={{ background: "rgba(31,110,76,0.12)", border: "1px solid var(--cps-border)" }}
                            data-testid="sf-includes-card"
                        >
                            <div className="label-mono" style={{ color: "var(--cps-lime)" }}>— WHAT'S INCLUDED</div>
                            <ul className="mt-6 space-y-3" style={{ color: "var(--cps-cream-dim)" }}>
                                {INCLUDED.map((item) => (
                                    <li key={item} className="flex items-start gap-3">
                                        <span className="w-1.5 h-1.5 mt-2" style={{ background: "var(--cps-lime)" }} />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                            <div className="mt-8 pt-6 border-t" style={{ borderColor: "var(--cps-border)" }}>
                                <div className="label-mono text-[10px] mb-3" style={{ color: "var(--cps-cream-dim)" }}>NOT INCLUDED</div>
                                <ul className="space-y-2 text-sm" style={{ color: "rgba(245,239,216,0.55)" }}>
                                    {EXCLUDED.map((item) => (
                                        <li key={item} className="flex items-start gap-3">
                                            <span className="w-1.5 h-1.5 mt-2" style={{ background: "var(--cps-border)" }} />
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="mt-8 pt-6 border-t space-y-4" style={{ borderColor: "var(--cps-border)" }}>
                                <button
                                    type="button"
                                    onClick={() => setBrochure({
                                        url: CPS_BROCHURE_URL,
                                        program_slug: "cps-2026",
                                        program_name: "Climate Power Summit 2026",
                                        interest_category: "Self-Funded Delegate — ₹32,000",
                                    })}
                                    className="label-mono text-[11px] inline-flex items-center gap-2 underline underline-offset-4"
                                    style={{ color: "var(--cps-lime)" }}
                                    data-testid="sf-brochure-btn"
                                >
                                    Download the summit brochure <Download className="w-3.5 h-3.5" />
                                </button>
                                <div>
                                    <Link
                                        to="/apply/cps-2026"
                                        className="label-mono text-[11px] inline-flex items-center gap-1 underline underline-offset-4"
                                        style={{ color: "var(--cps-cream-dim)" }}
                                        data-testid="sf-scholarship-link"
                                    >
                                        Applying for a scholarship instead? Use the application pathway <ArrowUpRight className="w-3.5 h-3.5" />
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </aside>
                </div>
            </div>
            <BrochureDialog brochure={brochure} onClose={() => setBrochure(null)} />
        </section>
    );
}
